import * as Api from '@ViewModels';
import * as React from 'react';
import { SalesLeadAIContext } from '../../../extViewmodels/sdk/models/AI';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import { useAIConversationEnrichCreationContextMutation } from '../../../queries/AI/useAIConversationEnrichCreationContextMutation';
import { useAIConversationChatMessageQuery } from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import {
	COACH_ASSISTANT_NAME,
	LIVE_SUGGESTION_PROMPT_HEADER,
	NO_NEW_TRANSCRIPT_RETRY,
	NO_TRANSCRIPT_YET,
} from './prompts';
import type { CoachUpstreamMessage } from './types';
import { nowId, useCoachConversationStream } from './useCoachConversationStream';

export type { CoachUpstreamMessage } from './types';

export interface UseRealtimeCoachingArgs {
	companyId?: string;
	leadServedId?: string;
	phoneCallId?: string;
}

/**
 * Options for the hook itself (vs. options for a specific `createNew` call).
 * `companyId` here is used to attach the `SalesLeadAIContext` to every
 * outbound stream so the AI has lead-scoped data on the server side — even
 * for sends that happen after a resume.
 */
/**
 * Where a freshly created conversation is in its opening (auto-start) turn:
 * `awaitingSend` until the first stream goes out carrying `autoStart`,
 * `streaming` until that stream settles, `none` otherwise (settled, resumed,
 * or no conversation).
 */
export type OpeningPhase = 'none' | 'awaitingSend' | 'streaming';

export interface UseRealtimeCoachingOptions {
	companyId?: string;
}

export interface UseRealtimeCoachingApi {
	coachingMessages: CoachUpstreamMessage[];
	conversationId: string | null;
	isCreatingConversation: boolean;
	isLoadingHistory: boolean;
	isAIResponding: boolean;
	/** True if the active conversation was resumed from the cache (vs newly created). */
	wasResumed: boolean;
	/** Server-resolved template of a freshly created conversation; null when resumed or unnamed. */
	agentTemplateName: string | null;
	openingPhase: OpeningPhase;
	/** Synchronous reads of the active conversation and create, current even before the next render. */
	getConversationId(): string | null;
	getIsCreating(): boolean;
	/** Synchronous read of {@link openingPhase}, current even before the next render. */
	getOpeningPhase(): OpeningPhase;
	/**
	 * Create a new server-side conversation. The starter prompt is injected
	 * server-side on the first user-message stream (via `autoStart: true`), not
	 * at creation time, so resumed conversations don't get a redundant prompt.
	 */
	createNew(args: UseRealtimeCoachingArgs): Promise<string | null>;
	/** Adopt an existing conversation and load its message history. */
	resume(conversationId: string): void;
	/** Returns the send's request id, which the reply carries as `requestId`, or null if nothing was sent. */
	sendDirectQuestion(question: string): string | null;
	/**
	 * Send the opening auto-start turn with no message of its own; the server replies with the lead's
	 * stored Game Plan. Returns the request id, or null (sending nothing) when no auto-start is pending.
	 */
	sendOpening(): string | null;
	sendSystemMessage(message: string): void;
	/**
	 * Append a finalized transcript entry to the local message stream so the UI
	 * can render it and `endCall` can detect that real transcription happened.
	 * Does NOT auto-stream the entry to the AI — the caller decides when to
	 * batch transcript content (typically via the end-of-call summary).
	 */
	addTranscriptEntry(entry: { id: string; speaker: string; text: string; timestamp: Date }): void;
	/**
	 * Format and send any transcript entries accumulated since the last call
	 * (or since `addTranscriptEntry` started). Returns the number of entries
	 * flushed; callers can use this to release a queue lock if no transcript
	 * was actually pending.
	 */
	sendTranscriptSuggestion(): number;
	/**
	 * The rep's own "Get suggestion" click. Unlike {@link sendTranscriptSuggestion} it sends even with no new
	 * transcript, asking for a different suggestion instead, and can carry a one-shot `instruction` that
	 * steers the reply. Returns false only when there is no conversation to send to.
	 */
	sendRequestedSuggestion(instruction?: string): boolean;
	/**
	 * Fire the end-of-call summary action, attaching any transcript entries that
	 * were never pushed upstream (the whole call when auto-suggestions are off,
	 * the tail after the last suggestion when they're on). Without that attach
	 * the BE would summarize a conversation whose only content is the Game Plan —
	 * `addTranscriptEntry` keeps entries client-side by design.
	 */
	requestPostCallSummary(): void;
	enrichCreationContext(params: { phoneCallId?: string; leadServedId?: string; source?: string }): void;
	reset(): void;
}

const formatTranscriptEntries = (entries: Array<{ speaker: string; text: string; timestamp: Date }>): string =>
	entries.map(e => `[${e.timestamp.toLocaleTimeString()}] ${e.speaker}: ${e.text}`).join('\n');

/**
 * Map a persisted `AIChatMessage` (from the chat-message GET) into the local
 * `CoachUpstreamMessage` shape used by the focused view. Persisted messages
 * are never streaming. `messageType` is left unset because the persisted
 * record doesn't carry it; consumers that need to distinguish transcript vs
 * direct-question entries must rely on text-content heuristics or wait for
 * a server-side schema change.
 */
function fromPersistedMessage(m: Api.AIChatMessage): CoachUpstreamMessage {
	return {
		id: m.id || nowId('persisted'),
		text: m.text ?? '',
		timestamp: new Date(),
		isStreaming: false,
		role: m.role === Api.AIChatRole.Assistant ? 'assistant' : 'user',
	};
}

/**
 * Focused-view-owned realtime coaching hook. Replaces the upstream
 * `useTysonRealtimeCoaching` + `TysonCoachingContext` pair for callers under
 * `focused-view/`. Reuses only the underlying HTTP adapters
 * (`useAIAssistantCreateConversationMutation`,
 * `useAIConversationEnrichCreationContextMutation`,
 * `useAIConversationChatMessageQuery`, `useStreamingResponseMutation`); all
 * conversation state, message accumulation, and lifecycle is owned here.
 *
 * Two entry points:
 *   - `createNew(args)` POSTs to `ai/assistant/{name}/conversation` and
 *     returns the new id. Does **not** auto-fire a bootstrap message — instead
 *     it arms `pendingAutoStartRef` so the first outbound stream carries
 *     `autoStart: true` and the server-side agent injects its starter prompt
 *     (typically triggered by the first `before_call` keyword pin).
 *   - `resume(id)` adopts an existing id and loads the persisted message
 *     history via the chat-message query. AutoStart is suppressed because the
 *     starter prompt is already in the conversation history.
 *
 * Outbound user messages stream through `aiPromptMutation`; server-handled
 * actions (e.g. the post-call summary) go through the same endpoint as an
 * `AIActionRequest`. The SSE `Response` events accumulate into
 * `coachingMessages`. Streamed tokens are intentionally NOT mirrored into the
 * chat-message react-query cache — the server persists outbound messages, so a
 * future `resume(id)` after reload backfills the authoritative history.
 */
export function useRealtimeCoaching(opts?: UseRealtimeCoachingOptions): UseRealtimeCoachingApi {
	const [conversationId, setConversationId] = React.useState<string | null>(null);
	const [coachingMessages, setCoachingMessages] = React.useState<CoachUpstreamMessage[]>([]);
	const [wasResumed, setWasResumed] = React.useState(false);
	const [agentTemplateName, setAgentTemplateName] = React.useState<string | null>(null);
	const conversationIdRef = React.useRef<string | null>(null);
	// `companyId` is read by `sendStream` to attach a `SalesLeadAIContext` on
	// every outbound stream. We keep it in a ref synced via an effect so the
	// callback dependency closure stays stable while still seeing the latest
	// value across renders.
	const companyIdRef = React.useRef<string | undefined>(opts?.companyId);
	React.useEffect(() => {
		companyIdRef.current = opts?.companyId;
	}, [opts?.companyId]);
	// Tracks whether the next outbound stream should set `autoStart: true` so
	// the server-side agent injects its starter prompt. Set to `true` by
	// `createNew()`, set to `false` by `resume()` (the existing conversation
	// already has a starter prompt) and after the first stream consumes it.
	// The backend rejects `autoStart: true` once messages exist, so the ref
	// must be flipped before the second stream.
	const pendingAutoStartRef = React.useRef(false);
	const creatingRef = React.useRef(false);
	// Bumped by `reset` so a create that resolves after a lead switch can tell it is stale.
	const sessionRef = React.useRef(0);
	const openingPhaseRef = React.useRef<OpeningPhase>('none');
	const [openingPhase, setOpeningPhaseState] = React.useState<OpeningPhase>('none');
	const setOpeningPhase = React.useCallback((next: OpeningPhase) => {
		openingPhaseRef.current = next;
		setOpeningPhaseState(next);
	}, []);
	const getOpeningPhase = React.useCallback(() => openingPhaseRef.current, []);
	const getConversationId = React.useCallback(() => conversationIdRef.current, []);
	const getIsCreating = React.useCallback(() => creatingRef.current, []);
	// Tracks which conversationId we've already backfilled history for. The
	// historyQuery's `data` reference can change after the initial fetch (e.g.
	// react-query refetch on focus, or any other consumer mutating the cache),
	// and we must NOT re-replace local state on those subsequent changes —
	// doing so would wipe streaming and user-composed messages.
	const backfilledIdRef = React.useRef<string | null>(null);
	// Unsent transcript entries waiting for the next "ask for a suggestion"
	// dispatch. Populated by `addTranscriptEntry`, drained by
	// `sendTranscriptSuggestion`. The user-visible chat already has each entry
	// as its own `messageType: 'transcript'` message via `addTranscriptEntry`;
	// this ref only powers the AI batch send.
	const pendingTranscriptRef = React.useRef<Array<{ speaker: string; text: string; timestamp: Date }>>([]);
	// Synchronous id dedup for `addTranscriptEntry`. The transcript pipeline
	// CAN emit two `final: true` events for the same utterance (Twilio
	// `lastSentence` markers etc.) and the React state update pattern alone
	// can't distinguish a duplicate from a genuine re-render before commit.
	// A ref-tracked Set checked synchronously fixes both.
	const seenTranscriptIdsRef = React.useRef<Set<string>>(new Set());
	const lastSuggestedConvoIdRef = React.useRef<string | null>(null);

	React.useEffect(() => {
		conversationIdRef.current = conversationId;
	}, [conversationId]);

	const createMutation = useAIAssistantCreateConversationMutation({
		onError: () => {
			// Caller observes via the returned promise / `isCreatingConversation`.
		},
	});

	const enrichMutation = useAIConversationEnrichCreationContextMutation({
		onError: () => {},
	});

	// Backfill on resume. `enabled` is gated on `wasResumed && conversationId`
	// so we don't fetch for new conversations (whose history is empty anyway).
	const historyQuery = useAIConversationChatMessageQuery({
		id: conversationId ?? '',
		enabled: wasResumed && !!conversationId,
		staleTime: Infinity,
	});

	React.useEffect(() => {
		if (!wasResumed) return;
		if (!conversationId) return;
		if (backfilledIdRef.current === conversationId) return;
		const data = historyQuery.data;
		if (!data) return;
		backfilledIdRef.current = conversationId;
		setCoachingMessages(data.map(fromPersistedMessage));
	}, [wasResumed, conversationId, historyQuery.data]);

	const settleOpening = (chatRequest: Api.AIChatRequest) => {
		if ((chatRequest as Api.AIMessageRequest).autoStart && openingPhaseRef.current === 'streaming') {
			setOpeningPhase('none');
		}
	};

	// SSE stream for outbound user messages and post-call summary actions. The shared helper drops
	// events from a stream whose conversation is no longer active: on a lead switch `reset()` nulls
	// `conversationIdRef` and `createNew`/`resume` repoints it, so a previous lead's still-open stream
	// (e.g. a post-call summary) can't append into the new lead's panel. Streamed tokens are
	// intentionally NOT mirrored into the chat-message react-query cache — every cache write re-fired
	// the history backfill and wiped local user messages; the server persists them anyway.
	const aiPromptMutation = useCoachConversationStream({
		conversationIdRef,
		setMessages: setCoachingMessages,
		onSuccess: params => settleOpening(params.chatRequest),
		// A failed opening still releases the pins held behind it; stranding them helps no one.
		onError: (_error, params) => settleOpening(params.chatRequest),
	});

	// Stable handle on the streaming mutation's `reset` (which aborts the
	// in-flight fetch via its AbortController). `useStreamingResponseMutation`
	// returns a fresh object each render, so we mirror its `reset` into a ref to
	// keep our own `reset` callback dependency-free and stable.
	const abortStreamRef = React.useRef(aiPromptMutation.reset);
	abortStreamRef.current = aiPromptMutation.reset;

	/**
	 * Internal: send a stream request, attaching `autoStart: true` on the very
	 * first send for a new conversation so the server-side agent injects its
	 * starter prompt. Resumed conversations skip `autoStart` because the
	 * starter prompt is already in the persisted history.
	 */
	const sendStream = React.useCallback(
		(convoId: string, userMessages: Api.AIChatMessage[], contexts?: Api.AIContext[]): string => {
			const requestId = nowId('req');
			const autoStart = pendingAutoStartRef.current;
			if (autoStart) {
				pendingAutoStartRef.current = false;
				setOpeningPhase('streaming');
			}

			// Attach `SalesLeadAIContext({companyId})` to every outbound stream
			// (mirrors legacy `useTysonRealtimeCoaching`). The server uses this
			// to load lead-scoped data — without it the AI has no lead context
			// even though the conversation was created with `companyId`. If the
			// caller already supplied a `SalesLeadAIContext`, we don't duplicate.
			const mergedContexts: Api.AIContext[] = [...(contexts ?? [])];
			const companyId = companyIdRef.current;
			if (companyId) {
				const hasLeadContext = mergedContexts.some(c => (c as { _type?: string })._type === 'SalesLeadAIContext');
				if (!hasLeadContext) {
					mergedContexts.push({ _type: 'SalesLeadAIContext', companyId } as SalesLeadAIContext);
				}
			}

			aiPromptMutation.mutate({
				convoId,
				requestId,
				chatRequest: {
					...(mergedContexts.length > 0 ? { contexts: mergedContexts } : {}),
					...(autoStart ? { autoStart: true } : {}),
					...(userMessages.length > 0 ? { chatMessages: userMessages } : {}),
				} as Api.AIMessageRequest,
			});
			return requestId;
		},
		[aiPromptMutation, setOpeningPhase]
	);

	const createNew = React.useCallback(
		async (args: UseRealtimeCoachingArgs): Promise<string | null> => {
			if (!args.companyId) return null;
			// A ref, not `createMutation.isPending`: the pending flag reaches callers a render late, and a
			// second bootstrap in that gap created a second conversation that received the pins too.
			if (creatingRef.current) return null;
			creatingRef.current = true;
			const session = sessionRef.current;
			try {
				const conversation = await createMutation.mutateAsync({
					assistantName: COACH_ASSISTANT_NAME,
					phoneCallId: args.phoneCallId,
					companyId: args.companyId,
					leadServedId: args.leadServedId,
				});
				// A lead switch reset us mid-create; this conversation belongs to the previous lead.
				if (session !== sessionRef.current) return null;
				if (!conversation?.id) return null;
				pendingAutoStartRef.current = true;
				setOpeningPhase('awaitingSend');
				setWasResumed(false);
				setAgentTemplateName(conversation.agentTemplateName || null);
				setCoachingMessages([]);
				// Update the ref synchronously (not just via the commit effect) so
				// the `onEvent` convoId guard sees the new id during the render gap
				// before the effect runs — otherwise the first tokens of the new
				// conversation's own stream could be rejected as "stale".
				conversationIdRef.current = conversation.id;
				setConversationId(conversation.id);
				return conversation.id;
			} catch {
				return null;
			} finally {
				if (session === sessionRef.current) creatingRef.current = false;
			}
		},
		[createMutation, setOpeningPhase]
	);

	const resume = React.useCallback(
		(id: string) => {
			pendingAutoStartRef.current = false;
			setOpeningPhase('none');
			setWasResumed(true);
			setAgentTemplateName(null);
			setCoachingMessages([]); // history backfill effect populates from query data
			// Keep the ref in lockstep with state (see `createNew`) so the stale-
			// stream guard resolves correctly immediately after a resume.
			conversationIdRef.current = id;
			setConversationId(id);
		},
		[setOpeningPhase]
	);

	const sendDirectQuestion = React.useCallback(
		(question: string): string | null => {
			const convoId = conversationIdRef.current;
			const trimmed = question.trim();
			if (!convoId || !trimmed) return null;
			const userMessage: CoachUpstreamMessage = {
				id: nowId('question'),
				text: trimmed,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'question',
			};
			setCoachingMessages(prev => [...prev, userMessage]);
			const wrapped = `The sales rep is asking you a direct question (not from the call transcript):\n${trimmed}`;
			// `sendStream` itself attaches `SalesLeadAIContext({companyId})` (when
			// the hook was called with a `companyId`), so the AI sees lead-scoped
			// data on every send — including this one. We do not pass an
			// explicit `contexts` array here.
			return sendStream(convoId, [{ role: Api.AIChatRole.User, text: wrapped, id: null }]);
		},
		[sendStream]
	);

	const sendOpening = React.useCallback((): string | null => {
		const convoId = conversationIdRef.current;
		if (!convoId || !pendingAutoStartRef.current) return null;
		return sendStream(convoId, []);
	}, [sendStream]);

	const sendSystemMessage = React.useCallback(
		(message: string) => {
			const convoId = conversationIdRef.current;
			const trimmed = message.trim();
			if (!convoId || !trimmed) return;
			const sysMessage: CoachUpstreamMessage = {
				id: nowId('system'),
				text: trimmed,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'system',
			};
			setCoachingMessages(prev => [...prev, sysMessage]);
			sendStream(convoId, [{ role: Api.AIChatRole.User, text: trimmed, id: null }]);
		},
		[sendStream]
	);

	const addTranscriptEntry = React.useCallback(
		(entry: { id: string; speaker: string; text: string; timestamp: Date }) => {
			const trimmed = entry.text.trim();
			if (!trimmed) return;
			// Synchronous dedup — see `seenTranscriptIdsRef` for why this isn't
			// done inside the setCoachingMessages updater.
			if (seenTranscriptIdsRef.current.has(entry.id)) return;
			seenTranscriptIdsRef.current.add(entry.id);

			const formatted = `${entry.speaker}: ${trimmed}`;
			const message: CoachUpstreamMessage = {
				id: entry.id,
				text: formatted,
				timestamp: entry.timestamp,
				isStreaming: false,
				role: 'user',
				messageType: 'transcript',
			};
			setCoachingMessages(prev => [...prev, message]);
			pendingTranscriptRef.current.push({
				speaker: entry.speaker,
				text: trimmed,
				timestamp: entry.timestamp,
			});
		},
		[]
	);

	const sendTranscriptSuggestion = React.useCallback((): number => {
		const convoId = conversationIdRef.current;
		if (!convoId) return 0;
		const entries = pendingTranscriptRef.current;
		if (entries.length === 0) return 0;
		pendingTranscriptRef.current = [];
		const formatted = formatTranscriptEntries(entries);
		// The "LIVE COACHING REQUEST" header is the contract documented in
		// `aida/constants/coachingContract.ts`: when the BE-side
		// coach agent sees that header, it must produce a tactical in-the-
		// moment suggestion, NOT a summary. Without this header, a previous
		// `CoachPostCallSummaryAction` earlier in the conversation history
		// can bias the model into summary mode for subsequent transcript
		// batches.
		const text = `${LIVE_SUGGESTION_PROMPT_HEADER}\n\n${formatted}`;
		// Don't append a local user message — each entry is already in the chat
		// as `messageType: 'transcript'` via `addTranscriptEntry`. We just push
		// the batch text up to the AI so it has the recent context for its
		// suggestion reply.
		sendStream(convoId, [{ role: Api.AIChatRole.User, text, id: null }]);
		lastSuggestedConvoIdRef.current = convoId;
		return entries.length;
	}, [sendStream]);

	const sendRequestedSuggestion = React.useCallback(
		(instruction?: string): boolean => {
			const convoId = conversationIdRef.current;
			if (!convoId) return false;
			const entries = pendingTranscriptRef.current;
			pendingTranscriptRef.current = [];
			const steer = instruction?.trim();
			let body: string;
			if (entries.length > 0) body = formatTranscriptEntries(entries);
			else if (steer) body = '';
			else body = lastSuggestedConvoIdRef.current === convoId ? NO_NEW_TRANSCRIPT_RETRY : NO_TRANSCRIPT_YET;
			const parts = [LIVE_SUGGESTION_PROMPT_HEADER, steer ? `Rep instruction: ${steer}` : '', body];
			// The header must lead: the BE coach agent gates tactical suggestions on the opening line.
			const text = parts.filter(Boolean).join('\n\n');
			sendStream(convoId, [{ role: Api.AIChatRole.User, text, id: null }]);
			lastSuggestedConvoIdRef.current = convoId;
			return true;
		},
		[sendStream]
	);

	const requestPostCallSummary = React.useCallback(() => {
		const convoId = conversationIdRef.current;
		if (!convoId) return;
		// Drain whatever transcript never made it upstream and ride it along on the
		// action. With auto-suggestions off that's the entire call; with them on
		// it's whatever the lead/rep said after the last suggestion. Draining here
		// (dispatch time) rather than when the summary was queued means utterances
		// that land while the queue waits on an in-flight stream are included too.
		const pending = pendingTranscriptRef.current;
		pendingTranscriptRef.current = [];
		const formattedTranscript = pending.length > 0 ? formatTranscriptEntries(pending) : undefined;
		// Fire the structured action; the BE handler owns the summary
		// instruction text. The `_type: 'AIActionRequest'` discriminant is
		// REQUIRED: the BE deserializes `AIChatRequest` polymorphically and
		// falls back to `AIMessageRequest` when `_type` is absent, which then
		// 400s with "Message request must include messages".
		//
		// `autoStart` is NOT attached — the BE rejects it on a non-message
		// request ("AutoStart requires an AIMessageRequest"). But we still
		// clear any pending autoStart: sending this summary creates messages,
		// so a later stream must not try to boot a now-non-empty conversation.
		pendingAutoStartRef.current = false;
		if (openingPhaseRef.current === 'awaitingSend') setOpeningPhase('none');
		const action = {
			_type: 'CoachPostCallSummaryAction',
			...(formattedTranscript ? { transcript: formattedTranscript } : {}),
		} as Api.CoachPostCallSummaryAction;
		aiPromptMutation.mutate({
			convoId,
			requestId: nowId('req'),
			chatRequest: {
				_type: 'AIActionRequest',
				action,
			} as Api.AIActionRequest,
		});
	}, [aiPromptMutation, setOpeningPhase]);

	const enrichCreationContext = React.useCallback(
		(params: { phoneCallId?: string; leadServedId?: string; source?: string }) => {
			const convoId = conversationIdRef.current;
			if (!convoId) return;
			if (!params.phoneCallId && !params.leadServedId && !params.source) return;
			enrichMutation.mutate({
				assistantName: COACH_ASSISTANT_NAME,
				conversationId: convoId,
				...params,
			});
		},
		[enrichMutation]
	);

	const reset = React.useCallback(() => {
		// Abort any in-flight stream first so the prior lead's open SSE (e.g. a
		// post-call summary) stops emitting. The `onEvent` convoId guard catches
		// any tokens that race past the abort. Note the abort rejects the stream
		// into the streaming mutation's `onError` (currently a no-op) — if an
		// `onError` handler is ever added there, it must apply the same stale-
		// convoId guard so an aborted prior-lead stream can't surface on the new
		// lead's panel.
		abortStreamRef.current();
		sessionRef.current++;
		creatingRef.current = false;
		setConversationId(null);
		conversationIdRef.current = null;
		setCoachingMessages([]);
		setWasResumed(false);
		setAgentTemplateName(null);
		pendingAutoStartRef.current = false;
		setOpeningPhase('none');
		backfilledIdRef.current = null;
		pendingTranscriptRef.current = [];
		seenTranscriptIdsRef.current = new Set();
	}, [setOpeningPhase]);

	return {
		coachingMessages,
		conversationId,
		isCreatingConversation: createMutation.isPending,
		isLoadingHistory: wasResumed && historyQuery.isLoading,
		isAIResponding: aiPromptMutation.isStreaming || aiPromptMutation.isLoading,
		wasResumed,
		agentTemplateName,
		openingPhase,
		getOpeningPhase,
		getConversationId,
		getIsCreating,
		createNew,
		resume,
		sendDirectQuestion,
		sendOpening,
		sendSystemMessage,
		addTranscriptEntry,
		sendTranscriptSuggestion,
		sendRequestedSuggestion,
		requestPostCallSummary,
		enrichCreationContext,
		reset,
	};
}
